import { getBusinessList, getLoadPending, getOpenBusinesses } from 'app-components/flux/business/businessSelectors';
import { rootUiActions } from 'app-components/flux/root-ui';
import { useFetchOnce } from 'app-components/hooks/utils';
import { getLogger } from 'app-components/utils/core';
import { InvoiceFormUpsertAction } from 'containers/InvoiceForm/actions';
import type { InvoiceFormModalProps } from 'containers/InvoiceForm/types';
import { BusinessUpgradeUpsertAction } from 'containers/UpgradeToBusiness/actions';
import { useEffect } from 'react';

import { businessActions, businessSelectors } from 'app-components/flux/business';
import { tracker } from 'app-components/utils/core';
import { useAppDispatch, useAppSelector, useAppStore } from 'hooks/redux';
import { useCallback } from 'react';

const log = getLogger('invoices/index.tsx');

export const useOpenBusinessUpgrade = () => {
  useFetchOnce(businessActions.getBusiness, businessSelectors, false);
  const dispatch = useAppDispatch();

  const noBusinesses = useAppSelector((state) => !getLoadPending(state) && getBusinessList(state).size === 0);

  const triggerBusinessUpgrade = useCallback(() => {
    dispatch(
      BusinessUpgradeUpsertAction({
        onClose: () => dispatch(rootUiActions.removeDialog('upgrade-to-business')),
        location: 'default',
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (noBusinesses) {
      log.debug('showBusinessUpgradeFlow');
      triggerBusinessUpgrade();
    }
  }, [dispatch, noBusinesses, triggerBusinessUpgrade]);

  return { triggerBusinessUpgrade, noBusinesses };
};

export const useOpenInvoice = (context: string) => {
  useFetchOnce(businessActions.getBusiness, businessSelectors, false);
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const openInvoice = useCallback(
    (props: InvoiceFormModalProps) => {
      dispatch(InvoiceFormUpsertAction({ context, ...props }));
    },
    [dispatch, context],
  );

  const openUpgradeDialog = useCallback(() => {
    log.debug('showBusinessUpgradeFlow');
    dispatch(
      BusinessUpgradeUpsertAction({
        onClose: () => dispatch(rootUiActions.removeDialog('upgrade-to-business')),
        location: 'default',
      }),
    );
  }, [dispatch]);

  const openInvoiceIfAllowed = useCallback(
    ({ invoice, ...other }: InvoiceFormModalProps = {}) => {
      const noOpenBusinesses = getOpenBusinesses(store.getState()).size === 0;
      const noBusinesses = getBusinessList(store.getState()).size === 0;

      if (noBusinesses || (noOpenBusinesses && !invoice)) {
        openUpgradeDialog();
      } else {
        if (!invoice) {
          tracker.track(tracker.events.createInvoiceStart);
        }
        openInvoice({ invoice, ...other });
      }
    },
    [openInvoice, openUpgradeDialog, store],
  );

  return openInvoiceIfAllowed;
};
